import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiArrowUpRight, FiChevronRight, FiAlertCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import SectionHead from '../components/SectionHead';
import CaseStudyCard from '../components/CaseStudyCard';
import ProductSpotlight from '../components/ProductSpotlight';
import AuditPromo from '../components/AuditPromo';
import FAQ from '../components/FAQ';
import CtaBand from '../components/CtaBand';
import JsonLd from '../components/JsonLd';
import NotFound from './NotFound';
import usePageMeta from '../utils/usePageMeta';
import { breadcrumbSchema, faqSchema, serviceSchema } from '../utils/schema';
import { getSolution } from '../data/solutions';
import { services, caseStudies, company } from '../data/site';

const SolutionPage = () => {
  const { solutionId } = useParams();
  const solution = getSolution(solutionId);

  usePageMeta(solution?.metaTitle, solution?.metaDescription);

  if (!solution) {
    return <NotFound />;
  }

  const path = `/solutions/${solution.id}`;
  const relatedWork = solution.relatedWork
    .map((title) => caseStudies.find((project) => project.title === title))
    .filter(Boolean);

  return (
    <>
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Solutions', path: '/solutions' },
            { name: solution.name, path }
          ]),
          serviceSchema({ name: solution.metaTitle, description: solution.metaDescription, path, audience: solution.name }),
          faqSchema(solution.faqs)
        ]}
      />

      <header className="page-header service-page-header">
        <motion.div
          className="container"
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/solutions">Solutions</Link>
            <FiChevronRight aria-hidden="true" />
            <span aria-current="page">{solution.name}</span>
          </nav>
          <div className="service-icon service-page-icon">
            <Icon name={solution.icon} />
          </div>
          <h1>{solution.headline}</h1>
          <p>{solution.intro}</p>
          <div className="btn-row">
            <Link to="/free-audit" className="btn btn-lg">
              Get a free digital audit <FiArrowRight />
            </Link>
            <a href={company.whatsapp} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">
              <FaWhatsapp /> Chat on WhatsApp
            </a>
          </div>
        </motion.div>
      </header>

      <section className="section">
        <div className="container split">
          <SectionHead
            eyebrow="Sound familiar?"
            title={`Common challenges for ${solution.name.toLowerCase()}`}
            subtitle="These are the problems we hear most often, and the ones we solve every day."
          />
          <ul className="challenge-list">
            {solution.challenges.map((challenge, index) => (
              <Reveal as="li" key={challenge} delay={index * 0.05}>
                <FiAlertCircle aria-hidden="true" />
                {challenge}
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <SectionHead
            eyebrow="How we help"
            title={`What Fedigma does for ${solution.name.toLowerCase()}`}
          />
          <div className="solution-services">
            {solution.services.map((item, index) => {
              const service = services.find((entry) => entry.id === item.id);
              if (!service) return null;
              return (
                <Reveal key={item.id} delay={index * 0.04}>
                  <Link to={service.link} className="solution-service">
                    <span className="service-icon"><Icon name={service.icon} /></span>
                    <div>
                      <h3>{service.title}</h3>
                      <p>{item.text}</p>
                    </div>
                    <FiArrowUpRight className="solution-service-arrow" aria-hidden="true" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {solution.showProduct && <ProductSpotlight showFeatures={false} />}

      {relatedWork.length > 0 && (
        <section className={`section ${solution.showProduct ? 'section-alt' : ''}`}>
          <div className="container">
            <div className="section-head-row">
              <SectionHead eyebrow="Our work" title="Institutes we already help" />
              <Link to="/work" className="text-link">
                View all work <FiArrowRight />
              </Link>
            </div>
            <Reveal className="work-grid">
              {relatedWork.map((project) => (
                <CaseStudyCard key={project.title} project={project} />
              ))}
            </Reveal>
          </div>
        </section>
      )}

      <AuditPromo />

      <FAQ items={solution.faqs} title={`Questions from ${solution.name.toLowerCase()}`} alt />

      <CtaBand title={`Let’s grow your ${solution.noun} together.`} />
    </>
  );
};

export default SolutionPage;
